import React, { useState, useEffect, forwardRef } from 'react';
import { clsx, createUseStyles, divide, negative } from '@pushpay/styles';
import { FocusLock, useForwardedRef } from '@pushpay/utils';
const borderTopWidth = '1px';

const getSlideAnimation = animationName => `150ms cubic-bezier(0.2, 0, 0.13, 1.5) forwards $${animationName}`;

const useStyles = createUseStyles(theme => ({
  root: {
    position: 'absolute',
    whiteSpace: 'nowrap',
    background: theme.colors['color-background-surface'],
    boxShadow: theme.ELEVATION.EIGHT,
    borderTop: `${borderTopWidth} solid ${theme.colors['color-border-surface']}`,
    borderRadius: theme.SHAPE.ROUNDED_CORNERS,
    zIndex: theme.ELEVATION.POPOVER.Z_INDEX,
    opacity: 0
  },
  slideDown: {
    top: '100%',
    marginTop: divide(theme.SPACING.GUTTER, 2),
    animation: getSlideAnimation('scale-down')
  },
  slideUp: {
    bottom: '100%',
    marginBottom: theme.SPACING.GUTTER,
    animation: getSlideAnimation('scale-up')
  },
  slideLeft: {
    top: negative(borderTopWidth),
    marginRight: divide(theme.SPACING.GUTTER, 2),
    animation: getSlideAnimation('scale-left')
  },
  slideRight: {
    top: negative(borderTopWidth),
    left: '100%',
    marginLeft: divide(theme.SPACING.GUTTER, 2),
    animation: getSlideAnimation('scale-right')
  },
  hidden: {
    display: 'none'
  },
  '@keyframes scale-down': {
    from: {
      opacity: 0,
      transform: 'scaleY(0.3) translateY(-50%)'
    },
    to: {
      opacity: 1,
      transform: 'scaleY(1) translateY(0)'
    }
  },
  '@keyframes scale-up': {
    from: {
      opacity: 0,
      transform: 'scaleY(0.3) translateY(50%)'
    },
    to: {
      opacity: 1,
      transform: 'scaleY(1) translateY(0)'
    }
  },
  '@keyframes scale-left': {
    from: {
      opacity: 0,
      transform: 'scaleX(0.3) translateX(50%)'
    },
    to: {
      opacity: 1,
      transform: 'scaleX(1) translateX(0)'
    }
  },
  '@keyframes scale-right': {
    from: {
      opacity: 0,
      transform: 'scaleX(0.3) translateX(-50%)'
    },
    to: {
      opacity: 1,
      transform: 'scaleX(1) translateX(0)'
    }
  }
}));
const Flyout = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    'data-pp-at-target': targetId,
    classes: classesProp,
    className: classNameProp,
    children,
    onKeyDown,
    disableResponsiveFlyoutDirection = false,
    preferredFlyoutDirection = 'bottom',
    preferredFlyoutAlignment = 'auto',
    isOpen = true,
    lockFocus = true,
    ...rest
  } = props;
  const [bindContainerRef, containerRef] = useForwardedRef(ref);
  const [additionalClassName, setAdditionalClassName] = useState(null);
  const extraLeftSpacing = 8;
  const classes = useStyles(classesProp);
  useEffect(() => {
    if (!isOpen) {
      setAdditionalClassName(classes.hidden);
      return;
    }

    let animationClass = classes.slideDown;

    if (containerRef.current) {
      const {
        top,
        bottom,
        left,
        right,
        height,
        width
      } = containerRef.current.getBoundingClientRect();

      switch (preferredFlyoutDirection) {
        case 'bottom':
          {
            const availableBottomSpace = window.innerHeight - top - height;
            animationClass = availableBottomSpace > 0 || disableResponsiveFlyoutDirection ? classes.slideDown : classes.slideUp;
            break;
          }

        case 'top':
          {
            const availableTopSpace = window.innerHeight - bottom - height;
            animationClass = availableTopSpace > 0 || disableResponsiveFlyoutDirection ? classes.slideUp : classes.slideDown;
            break;
          }

        case 'left':
          {
            const availableLeftSpace = left - width;
            animationClass = availableLeftSpace > 0 || disableResponsiveFlyoutDirection ? classes.slideLeft : classes.slideRight;
            break;
          }

        case 'right':
          {
            const availableRightSpace = window.innerWidth - right - width;
            animationClass = availableRightSpace > 0 || disableResponsiveFlyoutDirection ? classes.slideRight : classes.slideLeft;
            break;
          }

        default:
          throw new Error(`Direction prop "${preferredFlyoutDirection}" is not supported`);
      }

      if (animationClass === classes.slideLeft) {
        containerRef.current.style.left = `-${width + extraLeftSpacing}px`;
      }

      if (preferredFlyoutDirection === 'top' || preferredFlyoutDirection === 'bottom') {
        if (preferredFlyoutAlignment === 'right') {
          containerRef.current.style.right = `0`;
        }

        if (preferredFlyoutAlignment === 'left') {
          containerRef.current.style.left = `0`;
        }

        if (preferredFlyoutAlignment === 'auto') {
          containerRef.current.style.left = `-${width / 2}px`;
        }

        if (preferredFlyoutAlignment === 'stretch') {
          containerRef.current.style.left = `0`;
          containerRef.current.style.right = `0`;
        }
      }
    }

    setAdditionalClassName(animationClass);
  }, [classes.hidden, classes.slideDown, classes.slideLeft, classes.slideRight, classes.slideUp, containerRef, disableResponsiveFlyoutDirection, isOpen, preferredFlyoutAlignment, preferredFlyoutDirection]);
  const flyout = /*#__PURE__*/React.createElement("div", Object.assign({}, rest, {
    role: "presentation",
    className: clsx(classes.root, classNameProp, additionalClassName),
    "data-pp-at-target": targetId,
    ref: bindContainerRef,
    onKeyDown: onKeyDown
  }), children);

  if (lockFocus) {
    return /*#__PURE__*/React.createElement(FocusLock, {
      returnFocus: true,
      autoFocus: true
    }, flyout);
  }

  return flyout;
});
export default Flyout;